import * as THREE from 'three';

export class Cockpit {
  public group: THREE.Group;
  public steeringWheel: THREE.Group;
  public speedNeedle!: THREE.Mesh;
  public rpmNeedle!: THREE.Mesh;
  public gearTextCanvas!: HTMLCanvasElement;
  public gearTextTexture!: THREE.CanvasTexture;

  // Digital center cluster readouts
  public speedDigitalCanvas!: HTMLCanvasElement;
  public speedDigitalTexture!: THREE.CanvasTexture;
  public digitalGearMesh!: THREE.Mesh;
  public digitalSpeedMesh!: THREE.Mesh;

  // Smoothed needles for authentic mechanical dial inertia
  public displaySpeedKmh: number = 0;
  public displayRpm: number = 0;

  // Mirror meshes for render targets / reflection
  public interiorMirrorMesh!: THREE.Mesh;
  public leftMirrorMesh!: THREE.Mesh;
  public rightMirrorMesh!: THREE.Mesh;
  public mirrorGroup: THREE.Group = new THREE.Group();
  public interiorMirrorGroup: THREE.Group = new THREE.Group();

  // Instrument Cluster
  public clusterGroup: THREE.Group = new THREE.Group();

  // Roof headliner & interior A-pillars framing windshield
  public roofHeadlinerGroup: THREE.Group = new THREE.Group();

  // Mounted Navigation Smartphone
  public phoneGroup: THREE.Group = new THREE.Group();
  public phoneCanvas!: HTMLCanvasElement;
  public phoneTexture!: THREE.CanvasTexture;
  public phoneScreenMesh!: THREE.Mesh;
  private lastPhoneScore: number = -1;
  private lastPhoneGear: string = '';
  private lastPhoneSpeed: number = -1;
  private lastBlinkPhase: boolean = false;
  private lastBlinkerState: string = '';

  // 3D Blinker Arrow Canvases & Meshes
  public blinkerLeftCanvas!: HTMLCanvasElement;
  public blinkerLeftTexture!: THREE.CanvasTexture;
  public blinkerLeftMesh!: THREE.Mesh;
  public blinkerRightCanvas!: HTMLCanvasElement;
  public blinkerRightTexture!: THREE.CanvasTexture;
  public blinkerRightMesh!: THREE.Mesh;

  // Center console audio & vents
  public centerConsoleGroup: THREE.Group = new THREE.Group();
  public radioCanvas!: HTMLCanvasElement;
  public radioTexture!: THREE.CanvasTexture;

  // Warning lamps
  public blinkerLeftLamp!: THREE.Mesh;
  public blinkerRightLamp!: THREE.Mesh;
  public handbrakeLamp!: THREE.Mesh;
  public stallWarningLamp!: THREE.Mesh;

  // Examiner figure
  public examinerGroup!: THREE.Group;

  constructor() {
    this.group = new THREE.Group();
    this.steeringWheel = new THREE.Group();

    this.createRoofHeadlinerAndPillars();
    this.createDashboard();
    this.createSteeringWheel();
    this.createInstrumentCluster();
    this.createCenterConsole();
    this.createMountedSmartphone();
    this.createMirrors();
    this.createInteriorSeats();
  }

  /**
   * 1. Roof Headliner, Sun Visors & Interior A-Pillars
   * Accurately frames the windshield just like City Car Driving and real simulator cockpits.
   */
  private createRoofHeadlinerAndPillars() {
    this.roofHeadlinerGroup = new THREE.Group();

    const headlinerMat = new THREE.MeshStandardMaterial({
      color: 0x1f242d, // Dark charcoal automotive interior fabric
      roughness: 0.92,
      metalness: 0.05,
    });
    const visorMat = new THREE.MeshStandardMaterial({
      color: 0x272d38,
      roughness: 0.88,
      metalness: 0.05,
    });

    // 1. Ceiling headliner interior panel
    const headlinerGeom = new THREE.BoxGeometry(1.64, 0.04, 1.75);
    const headlinerMesh = new THREE.Mesh(headlinerGeom, headlinerMat);
    headlinerMesh.position.set(0, 1.45, -0.25);
    this.roofHeadlinerGroup.add(headlinerMesh);

    // 2. Windshield top header trim (slanted forward above rearview mirror)
    const headerGeom = new THREE.BoxGeometry(1.58, 0.08, 0.18);
    const headerMesh = new THREE.Mesh(headerGeom, headlinerMat);
    headerMesh.position.set(0, 1.41, 0.44);
    headerMesh.rotation.x = -0.35;
    this.roofHeadlinerGroup.add(headerMesh);

    // 3. Driver Sun Visor (Left side of vehicle = +0.38)
    const visorL = new THREE.Mesh(new THREE.BoxGeometry(0.48, 0.018, 0.18), visorMat);
    visorL.position.set(0.38, 1.39, 0.36);
    visorL.rotation.x = 0.22;
    this.roofHeadlinerGroup.add(visorL);

    // 4. Passenger Sun Visor (Right side of vehicle = -0.38)
    const visorR = new THREE.Mesh(new THREE.BoxGeometry(0.48, 0.018, 0.18), visorMat);
    visorR.position.set(-0.38, 1.39, 0.36);
    visorR.rotation.x = 0.22;
    this.roofHeadlinerGroup.add(visorR);

    // 5. Interior A-Pillars (Driver on Left = +0.78, Passenger on Right = -0.78 — Slender Golf/Polo trim)
    const aPillarGeom = new THREE.BoxGeometry(0.045, 0.88, 0.045);

    const aPillarIntL = new THREE.Mesh(aPillarGeom, headlinerMat);
    aPillarIntL.position.set(0.78, 1.05, 0.48);
    aPillarIntL.rotation.x = -0.48;
    aPillarIntL.rotation.z = 0.08;

    // SRS Curtain Airbag emboss badge on driver A-pillar
    const airbagBadgeGeom = new THREE.BoxGeometry(0.015, 0.05, 0.01);
    const airbagBadgeMat = new THREE.MeshStandardMaterial({ color: 0x334155, roughness: 0.6 });
    const airbagBadge = new THREE.Mesh(airbagBadgeGeom, airbagBadgeMat);
    airbagBadge.position.set(-0.025, 0.08, 0);
    aPillarIntL.add(airbagBadge);

    const aPillarIntR = new THREE.Mesh(aPillarGeom, headlinerMat);
    aPillarIntR.position.set(-0.78, 1.05, 0.48);
    aPillarIntR.rotation.x = -0.48;
    aPillarIntR.rotation.z = -0.08;

    this.roofHeadlinerGroup.add(aPillarIntL, aPillarIntR);
    this.group.add(this.roofHeadlinerGroup);
  }

  /**
   * 2. Sculpted Dashboard
   */
  private createDashboard() {
    const dashMaterial = new THREE.MeshStandardMaterial({
      color: 0x181b22, // Soft matte charcoal leather texture
      roughness: 0.85,
      metalness: 0.12,
    });
    const trimMat = new THREE.MeshStandardMaterial({
      color: 0x64748b, // Brushed silver metallic accent
      roughness: 0.35,
      metalness: 0.8,
    });

    // 1. Main Dashboard Top Cowl (sloping towards windshield base)
    const dashGeom = new THREE.BoxGeometry(1.68, 0.34, 0.72);
    const dashMesh = new THREE.Mesh(dashGeom, dashMaterial);
    dashMesh.position.set(0, 0.65, 0.58);
    dashMesh.rotation.x = 0.08;
    this.group.add(dashMesh);

    // Front dash top edge slope towards windshield glass
    const dashSlopeGeom = new THREE.BoxGeometry(1.66, 0.08, 0.32);
    const dashSlope = new THREE.Mesh(dashSlopeGeom, dashMaterial);
    dashSlope.position.set(0, 0.75, 0.82);
    dashSlope.rotation.x = -0.22;
    this.group.add(dashSlope);

    // Center Console Body
    const consoleGeom = new THREE.BoxGeometry(0.42, 0.54, 0.74);
    const consoleMesh = new THREE.Mesh(consoleGeom, dashMaterial);
    consoleMesh.position.set(0.0, 0.40, 0.35);
    this.group.add(consoleMesh);

    // Gear Selector Lever
    const shiftBase = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.03, 0.18), trimMat);
    shiftBase.position.set(0.0, 0.52, 0.28);
    const shiftStick = new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.014, 0.12, 12), trimMat);
    shiftStick.position.set(0.0, 0.58, 0.28);
    const shiftKnob = new THREE.Mesh(new THREE.SphereGeometry(0.026, 16, 16), dashMaterial);
    shiftKnob.position.set(0.0, 0.64, 0.28);
    this.group.add(shiftBase, shiftStick, shiftKnob);

    // Steering Column Stalks (Driver side = +0.38)
    const stalkMat = new THREE.MeshStandardMaterial({ color: 0x0f172a, roughness: 0.5 });
    const stalkL = new THREE.Mesh(new THREE.CylinderGeometry(0.007, 0.009, 0.12, 8), stalkMat);
    stalkL.position.set(0.52, 0.84, 0.32);
    stalkL.rotation.z = -Math.PI / 2.3;
    stalkL.rotation.x = -0.2;

    const stalkR = new THREE.Mesh(new THREE.CylinderGeometry(0.007, 0.009, 0.12, 8), stalkMat);
    stalkR.position.set(0.24, 0.84, 0.32);
    stalkR.rotation.z = Math.PI / 2.3;
    stalkR.rotation.x = -0.2;
    this.group.add(stalkL, stalkR);

    // Driver & Passenger Door Armrests
    const doorArmrestL = new THREE.Mesh(new THREE.BoxGeometry(0.10, 0.08, 0.55), dashMaterial);
    doorArmrestL.position.set(0.80, 0.58, -0.05);
    const doorArmrestR = new THREE.Mesh(new THREE.BoxGeometry(0.10, 0.08, 0.55), dashMaterial);
    doorArmrestR.position.set(-0.80, 0.58, -0.05);
    this.group.add(doorArmrestL, doorArmrestR);
  }

  /**
   * 3. Center Console with Audio/Radio & AC Vents
   */
  private createCenterConsole() {
    this.centerConsoleGroup = new THREE.Group();
    // Positioned in center stack, angled ~14° towards driver at +0.38
    this.centerConsoleGroup.position.set(-0.01, 0.885, 0.56);
    this.centerConsoleGroup.rotation.order = 'YXZ';
    this.centerConsoleGroup.rotation.y = Math.PI - 0.24;
    this.centerConsoleGroup.rotation.x = -0.10;

    const trimMat = new THREE.MeshStandardMaterial({
      color: 0x94a3b8, // Brushed aluminum trim
      roughness: 0.35,
      metalness: 0.75,
    });
    const darkDeckMat = new THREE.MeshStandardMaterial({
      color: 0x0f172a,
      roughness: 0.85,
      metalness: 0.2,
    });
    const glossBezelMat = new THREE.MeshStandardMaterial({
      color: 0x020617,
      roughness: 0.15,
      metalness: 0.85,
    });

    // 1. Center Stack Housing
    const stackHousing = new THREE.Mesh(new THREE.BoxGeometry(0.32, 0.32, 0.08), darkDeckMat);
    this.centerConsoleGroup.add(stackHousing);

    // 2. Dual Horizontal Air Conditioning Vents with Slats (above screen)
    const ventFrame = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.07, 0.02), trimMat);
    ventFrame.position.set(0.0, 0.11, 0.042);
    const slatMat = new THREE.MeshStandardMaterial({ color: 0x020617, roughness: 0.7 });
    for (let i = -1; i <= 1; i++) {
      const slat = new THREE.Mesh(new THREE.BoxGeometry(0.26, 0.005, 0.015), slatMat);
      slat.position.set(0, i * 0.018, 0.008);
      ventFrame.add(slat);
    }
    this.centerConsoleGroup.add(ventFrame);

    // Hazard Flasher Button (illuminated red triangle between vents)
    const hazardBody = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.032, 0.02), new THREE.MeshBasicMaterial({ color: 0xdc2626 }));
    hazardBody.position.set(0.0, 0.11, 0.052);
    this.centerConsoleGroup.add(hazardBody);

    // 3. Flush 16:9 MMI Infotainment Screen
    const mmiBezel = new THREE.Mesh(new THREE.BoxGeometry(0.26, 0.155, 0.015), glossBezelMat);
    mmiBezel.position.set(0.0, -0.015, 0.042);
    this.centerConsoleGroup.add(mmiBezel);

    if (typeof document === 'undefined') {
      this.radioTexture = new THREE.CanvasTexture({} as HTMLCanvasElement);
      this.group.add(this.centerConsoleGroup);
      return;
    }

    this.radioCanvas = document.createElement('canvas');
    this.radioCanvas.width = 512;
    this.radioCanvas.height = 288; // 16:9 aspect ratio
    const ctx = this.radioCanvas.getContext('2d')!;

    // High-contrast VW Discover Media / MMI GPS UI
    ctx.fillStyle = '#090d16';
    ctx.fillRect(0, 0, 512, 288);

    // Top status bar
    ctx.fillStyle = '#1e293b';
    ctx.fillRect(0, 0, 512, 40);
    ctx.fillStyle = '#38bdf8';
    ctx.font = 'bold 20px sans-serif';
    ctx.fillText('🧭 GPS 3D', 20, 28);
    ctx.fillStyle = '#f8fafc';
    ctx.font = 'bold 20px sans-serif';
    ctx.fillText('STADTZENTRUM • ZONE 30/50', 140, 28);
    ctx.fillStyle = '#94a3b8';
    ctx.font = '18px monospace';
    ctx.textAlign = 'right';
    ctx.fillText('21:00  LTE', 492, 28);
    ctx.textAlign = 'left';

    // Center map area & route path
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(16, 52, 480, 180);
    ctx.strokeStyle = '#1e3a8a';
    ctx.lineWidth = 14;
    ctx.beginPath();
    ctx.moveTo(120, 210);
    ctx.lineTo(256, 120);
    ctx.lineTo(390, 120);
    ctx.stroke();

    // Guidance arrow
    ctx.strokeStyle = '#38bdf8';
    ctx.lineWidth = 8;
    ctx.beginPath();
    ctx.moveTo(200, 170);
    ctx.lineTo(256, 120);
    ctx.lineTo(350, 120);
    ctx.stroke();

    // Speed limit badge (50 round sign)
    ctx.beginPath();
    ctx.arc(68, 142, 38, 0, Math.PI * 2);
    ctx.fillStyle = '#ffffff';
    ctx.fill();
    ctx.strokeStyle = '#dc2626';
    ctx.lineWidth = 10;
    ctx.stroke();
    ctx.fillStyle = '#0f172a';
    ctx.font = 'bold 36px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('50', 68, 142);
    ctx.textAlign = 'left';

    // Instruction text
    ctx.fillStyle = '#f8fafc';
    ctx.font = 'bold 24px sans-serif';
    ctx.fillText('Geradeaus folgen', 140, 100);
    ctx.fillStyle = '#94a3b8';
    ctx.font = '18px sans-serif';
    ctx.fillText('In 250m: Kreisverkehr erste Ausfahrt', 140, 135);

    // Bottom system bar
    ctx.fillStyle = '#0284c7';
    ctx.fillRect(0, 248, 512, 40);
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 18px sans-serif';
    ctx.fillText('FAHRSCHULE • STVO EXAM SYSTEM • LIVE TELEMETRY', 24, 274);

    this.radioTexture = new THREE.CanvasTexture(this.radioCanvas);
    const radioScreenMat = new THREE.MeshBasicMaterial({ map: this.radioTexture, side: THREE.DoubleSide });
    const radioScreen = new THREE.Mesh(new THREE.PlaneGeometry(0.24, 0.135), radioScreenMat);
    radioScreen.position.set(0.0, -0.015, 0.051);
    this.centerConsoleGroup.add(radioScreen);

    // Rotary Control Knobs below screen
    const knobGeom = new THREE.CylinderGeometry(0.014, 0.014, 0.018, 16);
    knobGeom.rotateX(Math.PI / 2);
    const knobL = new THREE.Mesh(knobGeom, trimMat);
    knobL.position.set(-0.09, -0.115, 0.045);
    const knobR = new THREE.Mesh(knobGeom, trimMat);
    knobR.position.set(0.09, -0.115, 0.045);
    this.centerConsoleGroup.add(knobL, knobR);

    this.group.add(this.centerConsoleGroup);
  }

  /**
   * 4. Contoured Steering Wheel with Silver Paddle Shifters & Spoke Accents
   */
  private createSteeringWheel() {
    const wheelMat = new THREE.MeshStandardMaterial({
      color: 0x11141a, // Soft ergonomic grip
      roughness: 0.65,
      metalness: 0.15,
    });
    const silverTrimMat = new THREE.MeshStandardMaterial({
      color: 0xd4d4d8,
      metalness: 0.85,
      roughness: 0.25,
    });

    // Torus rim
    const rimGeom = new THREE.TorusGeometry(0.18, 0.022, 16, 32);
    const rimMesh = new THREE.Mesh(rimGeom, wheelMat);

    // Center hub
    const hubGeom = new THREE.CylinderGeometry(0.065, 0.065, 0.04, 24);
    hubGeom.rotateX(Math.PI / 2);
    const hubMesh = new THREE.Mesh(hubGeom, wheelMat);

    // Fahrschule Logo emblem (Driver-facing: negative Z in wheel local space)
    const badgeGeom = new THREE.CircleGeometry(0.032, 24);
    const badgeMat = new THREE.MeshStandardMaterial({ color: 0x2563eb, metalness: 0.8, roughness: 0.2, side: THREE.DoubleSide });
    const badgeMesh = new THREE.Mesh(badgeGeom, badgeMat);
    badgeMesh.position.z = -0.022;
    badgeMesh.rotation.y = Math.PI;

    const badgeRingGeom = new THREE.RingGeometry(0.030, 0.034, 24);
    const badgeRing = new THREE.Mesh(badgeRingGeom, silverTrimMat);
    badgeRing.position.z = -0.023;
    badgeRing.rotation.y = Math.PI;

    // Spokes with silver accents
    const spokeGeom = new THREE.BoxGeometry(0.032, 0.16, 0.015);
    const spokeL = new THREE.Mesh(spokeGeom, wheelMat);
    spokeL.rotation.z = Math.PI / 2.8;
    spokeL.position.set(-0.08, -0.02, 0);

    const spokeTrimL = new THREE.Mesh(new THREE.BoxGeometry(0.024, 0.12, 0.018), silverTrimMat);
    spokeTrimL.position.z = -0.008;
    spokeL.add(spokeTrimL);

    const spokeR = new THREE.Mesh(spokeGeom, wheelMat);
    spokeR.rotation.z = -Math.PI / 2.8;
    spokeR.position.set(0.08, -0.02, 0);

    const spokeTrimR = new THREE.Mesh(new THREE.BoxGeometry(0.024, 0.12, 0.018), silverTrimMat);
    spokeTrimR.position.z = -0.008;
    spokeR.add(spokeTrimR);

    const spokeB = new THREE.Mesh(spokeGeom, wheelMat);
    spokeB.position.set(0, -0.09, 0);
    const spokeTrimB = new THREE.Mesh(new THREE.BoxGeometry(0.024, 0.10, 0.018), silverTrimMat);
    spokeTrimB.position.z = -0.008;
    spokeB.add(spokeTrimB);

    // Silver Paddle Shifters behind steering wheel (+Z in wheel local space)
    const paddleGeom = new THREE.BoxGeometry(0.03, 0.08, 0.008);
    const paddleL = new THREE.Mesh(paddleGeom, silverTrimMat);
    paddleL.position.set(-0.16, 0.04, 0.03);
    paddleL.rotation.z = 0.15;

    const paddleR = new THREE.Mesh(paddleGeom, silverTrimMat);
    paddleR.position.set(0.16, 0.04, 0.03);
    paddleR.rotation.z = -0.15;

    this.steeringWheel.add(rimMesh, hubMesh, badgeMesh, badgeRing, spokeL, spokeR, spokeB, paddleL, paddleR);

    // Left and Right hands (neatly placed at 9 & 3 positions, preserving test requirements)
    const sleeveMat = new THREE.MeshStandardMaterial({ color: 0x1e293b, roughness: 0.8 });
    const handMat = new THREE.MeshStandardMaterial({ color: 0xd4a373, roughness: 0.6 });
    const thumbMat = new THREE.MeshStandardMaterial({ color: 0xc89666, roughness: 0.6 });

    const handLGroup = new THREE.Group();
    handLGroup.position.set(-0.18, 0, 0);
    const palmL = new THREE.Mesh(new THREE.BoxGeometry(0.038, 0.048, 0.032), handMat);
    palmL.position.set(-0.008, 0, -0.008);
    const thumbL = new THREE.Mesh(new THREE.CylinderGeometry(0.009, 0.011, 0.032, 10), thumbMat);
    thumbL.position.set(0.016, 0.016, -0.012);
    thumbL.rotation.z = -0.4;
    const cuffL = new THREE.Mesh(new THREE.CylinderGeometry(0.026, 0.028, 0.06, 12), sleeveMat);
    cuffL.position.set(-0.015, -0.035, 0.005);
    handLGroup.add(palmL, thumbL, cuffL);

    const handRGroup = new THREE.Group();
    handRGroup.position.set(0.18, 0, 0);
    const palmR = new THREE.Mesh(new THREE.BoxGeometry(0.038, 0.048, 0.032), handMat);
    palmR.position.set(0.008, 0, -0.008);
    const thumbR = new THREE.Mesh(new THREE.CylinderGeometry(0.009, 0.011, 0.032, 10), thumbMat);
    thumbR.position.set(-0.016, 0.016, -0.012);
    thumbR.rotation.z = 0.4;
    const cuffR = new THREE.Mesh(new THREE.CylinderGeometry(0.026, 0.028, 0.06, 12), sleeveMat);
    cuffR.position.set(0.015, -0.035, 0.005);
    handRGroup.add(palmR, thumbR, cuffR);

    this.steeringWheel.add(handLGroup, handRGroup);

    // Left-hand drive steering wheel position (x = +0.38)
    this.steeringWheel.position.set(0.38, 0.84, 0.30);
    this.steeringWheel.rotation.x = -0.38;
    this.steeringWheel.visible = false; // Hidden by default for physical sim rigs (toggle with KeyO)
    this.group.add(this.steeringWheel);
  }

  // Sim-Rig Steering Wheel Visibility Toggle (hides 3D wheel & hands when using a physical sim wheel)
  public isSteeringWheelVisible: boolean = false;

  public setSteeringWheelVisible(visible: boolean) {
    this.isSteeringWheelVisible = visible;
    this.steeringWheel.visible = visible;
  }

  /**
   * 5. 360° SimDrive Instrument Cluster with Sculpted Aerodynamic Cowl
   * Features VW-style high-contrast dual gauges (260 km/h speed, Engine on pill, chrome bezels)
   */
  private createGaugeTexture(type: 'speed' | 'rpm'): THREE.CanvasTexture {
    if (typeof document === 'undefined') {
      return new THREE.CanvasTexture({} as HTMLCanvasElement);
    }
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;
    const ctx = canvas.getContext('2d');
    if (!ctx) return new THREE.CanvasTexture(canvas);

    const cx = 256;
    const cy = 256;
    const radius = 236;

    // Deep matte black background with metallic outer bezel
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.fillStyle = '#060913';
    ctx.fill();
    ctx.strokeStyle = '#94a3b8';
    ctx.lineWidth = 8;
    ctx.stroke();

    // Subtle radial gradient disc for realistic dial depth
    const discGrad = ctx.createRadialGradient(cx, cy, 60, cx, cy, radius - 8);
    discGrad.addColorStop(0, '#0f172a');
    discGrad.addColorStop(1, '#020617');
    ctx.fillStyle = discGrad;
    ctx.beginPath();
    ctx.arc(cx, cy, radius - 8, 0, Math.PI * 2);
    ctx.fill();

    const startAngle = Math.PI * 0.75;
    const totalSweep = Math.PI * 1.5;

    // Glowing subtle outer arc
    ctx.save();
    ctx.shadowColor = '#38bdf8';
    ctx.shadowBlur = 14;
    ctx.beginPath();
    ctx.arc(cx, cy, radius - 14, startAngle, startAngle + totalSweep);
    ctx.strokeStyle = '#0284c7';
    ctx.lineWidth = 4;
    ctx.stroke();
    ctx.restore();

    if (type === 'speed') {
      const maxSpeed = 260;
      const ticks = [0, 20, 40, 60, 80, 100, 120, 140, 160, 180, 200, 220, 240, 260];

      for (let s = 0; s <= maxSpeed; s += 10) {
        const angle = startAngle + (s / maxSpeed) * totalSweep;
        const isStVO = s === 30 || s === 50;
        const isMajor = ticks.includes(s);
        const tickLen = isStVO ? 34 : isMajor ? 26 : 14;

        const x1 = cx + Math.cos(angle) * (radius - 16 - tickLen);
        const y1 = cy + Math.sin(angle) * (radius - 16 - tickLen);
        const x2 = cx + Math.cos(angle) * (radius - 16);
        const y2 = cy + Math.sin(angle) * (radius - 16);

        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.strokeStyle = isStVO ? '#ef4444' : isMajor ? '#ffffff' : '#64748b';
        ctx.lineWidth = isStVO ? 6 : isMajor ? 4.5 : 2.5;
        ctx.stroke();

        if (isMajor) {
          const numR = radius - 68;
          const nx = cx + Math.cos(angle) * numR;
          const ny = cy + Math.sin(angle) * numR;
          ctx.font = 'bold 28px sans-serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillStyle = isStVO ? '#f87171' : '#ffffff';
          ctx.fillText(`${s}`, nx, ny);
        }
      }

      // km/h unit
      ctx.fillStyle = '#38bdf8';
      ctx.font = 'bold 22px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('km/h', cx, cy + 96);
    } else {
      // RPM Tachometer
      const maxRpm = 8;
      const redlineStart = startAngle + totalSweep * (6 / 8);

      // Redline glowing arc
      ctx.save();
      ctx.shadowColor = '#dc2626';
      ctx.shadowBlur = 16;
      ctx.beginPath();
      ctx.arc(cx, cy, radius - 14, redlineStart, startAngle + totalSweep);
      ctx.strokeStyle = '#ef4444';
      ctx.lineWidth = 7;
      ctx.stroke();
      ctx.restore();

      for (let r = 0; r <= maxRpm; r++) {
        const angle = startAngle + (r / maxRpm) * totalSweep;
        const isRed = r >= 6;
        const tickLen = 28;

        const x1 = cx + Math.cos(angle) * (radius - 16 - tickLen);
        const y1 = cy + Math.sin(angle) * (radius - 16 - tickLen);
        const x2 = cx + Math.cos(angle) * (radius - 16);
        const y2 = cy + Math.sin(angle) * (radius - 16);

        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.strokeStyle = isRed ? '#ef4444' : '#ffffff';
        ctx.lineWidth = isRed ? 5.5 : 4;
        ctx.stroke();

        const numR = radius - 68;
        const nx = cx + Math.cos(angle) * numR;
        const ny = cy + Math.sin(angle) * numR;
        ctx.font = 'bold 34px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = isRed ? '#ef4444' : '#ffffff';
        ctx.fillText(`${r}`, nx, ny);
      }

      ctx.fillStyle = '#94a3b8';
      ctx.font = 'bold 20px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('1/min x 1000', cx, cy + 96);
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.anisotropy = 4;
    return texture;
  }

  private drawBlinkerArrow(canvas: HTMLCanvasElement, isLeft: boolean, isOn: boolean) {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, 128, 128);

    ctx.save();
    ctx.translate(64, 64);
    if (!isLeft) {
      ctx.scale(-1, 1); // mirror for right arrow
    }

    // Arrow shape pointing left
    ctx.beginPath();
    ctx.moveTo(-44, 0);   // tip pointing left
    ctx.lineTo(8, -36);   // top corner
    ctx.lineTo(8, -16);   // inner top
    ctx.lineTo(46, -16);  // stem top
    ctx.lineTo(46, 16);   // stem bottom
    ctx.lineTo(8, 16);    // inner bottom
    ctx.lineTo(8, 36);    // bottom corner
    ctx.closePath();

    if (isOn) {
      ctx.shadowColor = '#22c55e';
      ctx.shadowBlur = 18;
      ctx.fillStyle = '#22c55e';
      ctx.fill();
      ctx.strokeStyle = '#86efac';
      ctx.lineWidth = 4;
      ctx.stroke();
    } else {
      ctx.fillStyle = 'rgba(15, 23, 42, 0.45)';
      ctx.fill();
      ctx.strokeStyle = 'rgba(71, 85, 105, 0.45)';
      ctx.lineWidth = 3;
      ctx.stroke();
    }
    ctx.restore();
  }

  private createInstrumentCluster() {
    this.clusterGroup = new THREE.Group();
    // Binnacle positioned directly behind the top half of the steering wheel rim
    this.clusterGroup.position.set(0.38, 0.890, 0.52);
    this.clusterGroup.rotation.order = 'YXZ';
    this.clusterGroup.rotation.y = Math.PI;
    this.clusterGroup.rotation.x = 0.20;
    this.group.add(this.clusterGroup);

    const clusterMat = new THREE.MeshBasicMaterial({ color: 0x070a12, side: THREE.DoubleSide });
    const clusterPanel = new THREE.Mesh(new THREE.PlaneGeometry(0.50, 0.24), clusterMat);
    clusterPanel.position.set(0, 0.01, 0);
    this.clusterGroup.add(clusterPanel);

    // 360° SimDrive Unified Aerodynamic Dashboard Cowl (matching real VW Golf cockpit binnacle)
    const cowlMat = new THREE.MeshStandardMaterial({
      color: 0x141822, // Matte soft-touch automotive composite
      roughness: 0.85,
      metalness: 0.15,
      side: THREE.DoubleSide,
    });
    const trimMat = new THREE.MeshStandardMaterial({
      color: 0xcfd8dc, // Brushed chrome / aluminum bezel trim
      roughness: 0.25,
      metalness: 0.85,
    });

    // 1. Continuous Swept Cowl Hood arching over both dials
    const cowlHoodGeom = new THREE.BoxGeometry(0.52, 0.030, 0.06);
    const cowlHood = new THREE.Mesh(cowlHoodGeom, cowlMat);
    cowlHood.position.set(0, 0.135, 0.01);
    cowlHood.rotation.x = 0.08;

    // Top brow sun-shield overhang (angled upwards away from line of sight to shade without blocking view)
    const browGeom = new THREE.BoxGeometry(0.50, 0.020, 0.04);
    const cowlBrow = new THREE.Mesh(browGeom, cowlMat);
    cowlBrow.position.set(0, 0.142, 0.035);
    cowlBrow.rotation.x = 0.10;

    // Left and right side binnacle cheeks
    const cheekGeom = new THREE.BoxGeometry(0.03, 0.24, 0.08);
    const cheekL = new THREE.Mesh(cheekGeom, cowlMat);
    cheekL.position.set(-0.25, 0.01, 0.02);
    const cheekR = new THREE.Mesh(cheekGeom, cowlMat);
    cheekR.position.set(0.25, 0.01, 0.02);

    this.clusterGroup.add(cowlHood, cowlBrow, cheekL, cheekR);

    // Chrome inner bezels around tachometer and speedometer dials
    const bezelGeom = new THREE.RingGeometry(0.076, 0.082, 32);
    const bezelL = new THREE.Mesh(bezelGeom, trimMat);
    bezelL.position.set(-0.11, 0.01, 0.007);
    const bezelR = new THREE.Mesh(bezelGeom, trimMat);
    bezelR.position.set(0.11, 0.01, 0.007);
    this.clusterGroup.add(bezelL, bezelR);

    // RPM Tachometer dial (Left side of driver cluster: local -X)
    const rpmTex = this.createGaugeTexture('rpm');
    const rpmDialMat = new THREE.MeshBasicMaterial({ map: rpmTex, transparent: true, side: THREE.DoubleSide });
    const rpmDial = new THREE.Mesh(new THREE.CircleGeometry(0.078, 32), rpmDialMat);
    rpmDial.position.set(-0.11, 0.01, 0.005);

    // Speedometer dial (Right side of driver cluster: local +X)
    const speedTex = this.createGaugeTexture('speed');
    const speedDialMat = new THREE.MeshBasicMaterial({ map: speedTex, transparent: true, side: THREE.DoubleSide });
    const speedDial = new THREE.Mesh(new THREE.CircleGeometry(0.078, 32), speedDialMat);
    speedDial.position.set(0.11, 0.01, 0.005);

    // Glowing Amber Needles
    const needleGeom = new THREE.BoxGeometry(0.004, 0.060, 0.003);
    needleGeom.translate(0, 0.026, 0);
    const needleMat = new THREE.MeshBasicMaterial({ color: 0xf97316, side: THREE.DoubleSide }); // Glowing amber-orange
    this.rpmNeedle = new THREE.Mesh(needleGeom, needleMat);
    this.rpmNeedle.position.set(-0.11, 0.01, 0.012);

    this.speedNeedle = new THREE.Mesh(needleGeom, needleMat);
    this.speedNeedle.position.set(0.11, 0.01, 0.012);

    // Warning Lamps (Handbrake & Stall)
    this.handbrakeLamp = new THREE.Mesh(new THREE.CircleGeometry(0.007, 12), new THREE.MeshBasicMaterial({ color: 0xb91c1c, side: THREE.DoubleSide }));
    this.handbrakeLamp.position.set(0, 0.05, 0.008);

    this.stallWarningLamp = new THREE.Mesh(new THREE.CircleGeometry(0.007, 12), new THREE.MeshBasicMaterial({ color: 0xeab308, side: THREE.DoubleSide }));
    this.stallWarningLamp.position.set(0, -0.03, 0.008);

    // Legacy compat lamps for tests
    this.blinkerLeftLamp = new THREE.Mesh(new THREE.CircleGeometry(0.004, 8), new THREE.MeshBasicMaterial({ color: 0x15803d, side: THREE.DoubleSide }));
    this.blinkerRightLamp = new THREE.Mesh(new THREE.CircleGeometry(0.004, 8), new THREE.MeshBasicMaterial({ color: 0x15803d, side: THREE.DoubleSide }));

    if (typeof document === 'undefined') {
      this.gearTextTexture = new THREE.CanvasTexture({} as HTMLCanvasElement);
      this.speedDigitalTexture = new THREE.CanvasTexture({} as HTMLCanvasElement);
      this.digitalGearMesh = new THREE.Mesh();
      this.digitalSpeedMesh = new THREE.Mesh();
      this.blinkerLeftMesh = new THREE.Mesh();
      this.blinkerRightMesh = new THREE.Mesh();
      this.clusterGroup.add(
        rpmDial,
        speedDial,
        this.rpmNeedle,
        this.speedNeedle,
        this.digitalGearMesh,
        this.digitalSpeedMesh,
        this.handbrakeLamp,
        this.stallWarningLamp
      );
      return;
    }

    // Authentic Indicator Light Arrows (Left and Right Green Arrows)
    this.blinkerLeftCanvas = document.createElement('canvas');
    this.blinkerLeftCanvas.width = 128;
    this.blinkerLeftCanvas.height = 128;
    this.drawBlinkerArrow(this.blinkerLeftCanvas, true, false);
    this.blinkerLeftTexture = new THREE.CanvasTexture(this.blinkerLeftCanvas);
    const bMatL = new THREE.MeshBasicMaterial({ map: this.blinkerLeftTexture, transparent: true, side: THREE.DoubleSide });
    this.blinkerLeftMesh = new THREE.Mesh(new THREE.PlaneGeometry(0.038, 0.038), bMatL);
    this.blinkerLeftMesh.position.set(-0.045, 0.07, 0.015);

    this.blinkerRightCanvas = document.createElement('canvas');
    this.blinkerRightCanvas.width = 128;
    this.blinkerRightCanvas.height = 128;
    this.drawBlinkerArrow(this.blinkerRightCanvas, false, false);
    this.blinkerRightTexture = new THREE.CanvasTexture(this.blinkerRightCanvas);
    const bMatR = new THREE.MeshBasicMaterial({ map: this.blinkerRightTexture, transparent: true, side: THREE.DoubleSide });
    this.blinkerRightMesh = new THREE.Mesh(new THREE.PlaneGeometry(0.038, 0.038), bMatR);
    this.blinkerRightMesh.position.set(0.045, 0.07, 0.015);

    // Digital Gear Display Canvas in Center of RPM Dial ('1', '2', 'D', 'P', 'R' + 'Engine on' status)
    this.gearTextCanvas = document.createElement('canvas');
    this.gearTextCanvas.width = 128;
    this.gearTextCanvas.height = 128;
    this.gearTextTexture = new THREE.CanvasTexture(this.gearTextCanvas);
    const gearMat = new THREE.MeshBasicMaterial({ map: this.gearTextTexture, transparent: true, side: THREE.DoubleSide });
    this.digitalGearMesh = new THREE.Mesh(new THREE.PlaneGeometry(0.046, 0.046), gearMat);
    this.digitalGearMesh.position.set(-0.11, 0.01, 0.015);

    // Digital Speed Display Canvas in Center of Speedometer
    this.speedDigitalCanvas = document.createElement('canvas');
    this.speedDigitalCanvas.width = 128;
    this.speedDigitalCanvas.height = 128;
    this.speedDigitalTexture = new THREE.CanvasTexture(this.speedDigitalCanvas);
    const digitalSpeedMat = new THREE.MeshBasicMaterial({ map: this.speedDigitalTexture, transparent: true, side: THREE.DoubleSide });
    this.digitalSpeedMesh = new THREE.Mesh(new THREE.PlaneGeometry(0.046, 0.046), digitalSpeedMat);
    this.digitalSpeedMesh.position.set(0.11, 0.01, 0.015);

    this.clusterGroup.add(
      rpmDial,
      speedDial,
      this.rpmNeedle,
      this.speedNeedle,
      this.digitalGearMesh,
      this.digitalSpeedMesh,
      this.blinkerLeftMesh,
      this.blinkerRightMesh,
      this.handbrakeLamp,
      this.stallWarningLamp
    );

    this.renderDigitalCluster('P', 0, true);
  }

  public renderDigitalCluster(gear: string, speedKmh: number, engineRunning: boolean = true) {
    // 1. Digital Gear Display (OLED inside Tachometer with 'Engine on' status pill)
    if (!this.gearTextCanvas) return;
    const gCtx = this.gearTextCanvas.getContext('2d');
    if (gCtx) {
      gCtx.clearRect(0, 0, 128, 128);
      gCtx.fillStyle = '#090d16';
      gCtx.beginPath();
      gCtx.roundRect(8, 8, 112, 112, 16);
      gCtx.fill();
      gCtx.strokeStyle = '#3b82f6';
      gCtx.lineWidth = 2.5;
      gCtx.stroke();

      const isReverse = gear === 'R';
      const isDrive = gear.startsWith('D') || gear === 'D';
      gCtx.fillStyle = isReverse ? '#ef4444' : isDrive ? '#ffffff' : '#fbbf24';
      gCtx.font = 'bold 50px sans-serif';
      gCtx.textAlign = 'center';
      gCtx.textBaseline = 'middle';
      const label = gear === 'D' ? 'D' : gear.startsWith('D') ? gear.slice(1) || '1' : gear;
      gCtx.fillText(label, 64, 48);

      // Status pill: Engine on (matching Photo 3 & 4)
      if (engineRunning) {
        gCtx.fillStyle = '#22c55e';
        gCtx.font = 'bold 15px sans-serif';
        gCtx.fillText('● Engine on', 64, 94);
      } else {
        gCtx.fillStyle = '#f59e0b';
        gCtx.font = 'bold 15px sans-serif';
        gCtx.fillText('○ Engine off', 64, 94);
      }
      this.gearTextTexture.needsUpdate = true;
    }

    // 2. Digital Speed Display (High-contrast OLED inside Speedometer)
    if (!this.speedDigitalCanvas) return;
    const sCtx = this.speedDigitalCanvas.getContext('2d');
    if (sCtx) {
      sCtx.clearRect(0, 0, 128, 128);
      sCtx.fillStyle = '#090d16';
      sCtx.beginPath();
      sCtx.roundRect(8, 8, 112, 112, 16);
      sCtx.fill();
      sCtx.strokeStyle = '#38bdf8';
      sCtx.lineWidth = 2.5;
      sCtx.stroke();

      sCtx.fillStyle = '#ffffff';
      sCtx.font = 'bold 54px sans-serif';
      sCtx.textAlign = 'center';
      sCtx.textBaseline = 'middle';
      sCtx.fillText(`${Math.round(Math.abs(speedKmh))}`, 64, 50);

      sCtx.font = 'bold 18px sans-serif';
      sCtx.fillStyle = '#38bdf8';
      sCtx.fillText('km/h', 64, 94);
      this.speedDigitalTexture.needsUpdate = true;
    }
  }

  /**
   * 6. Mounted Smartphone with Live StVO Navigation Display (Task 2)
   * Positioned on the left windshield / A-pillar bracket, matching the reference image.
   */
  private createMountedSmartphone() {
    this.phoneGroup = new THREE.Group();
    this.phoneGroup.visible = false; // Hidden in 360° simdrive cockpit to preserve unobstructed left door mirror view

    // 1. Windshield Suction Cup & Articulated Arm (Driver's Left Windshield / A-Pillar)
    const mountArmMat = new THREE.MeshStandardMaterial({ color: 0x1e293b, roughness: 0.5, metalness: 0.5 });
    const suctionCup = new THREE.Mesh(new THREE.CylinderGeometry(0.028, 0.032, 0.015, 16), mountArmMat);
    suctionCup.position.set(0.74, 1.02, 0.42);
    suctionCup.rotation.x = -0.48;

    const arm = new THREE.Mesh(new THREE.CylinderGeometry(0.008, 0.008, 0.12, 12), mountArmMat);
    arm.position.set(0.71, 0.99, 0.38);
    arm.rotation.z = -0.35;
    arm.rotation.y = -0.25;

    // 2. Smartphone Body (16:9 vertical smartphone aspect)
    const phoneBodyGeom = new THREE.BoxGeometry(0.096, 0.185, 0.008);
    const phoneBodyMat = new THREE.MeshStandardMaterial({ color: 0x090d16, roughness: 0.3, metalness: 0.7 });
    const phoneBody = new THREE.Mesh(phoneBodyGeom, phoneBodyMat);
    phoneBody.position.set(0.68, 0.96, 0.32);
    phoneBody.rotation.y = -0.42; // Angled comfortably towards driver's eyes
    phoneBody.rotation.x = -0.14;

    // 3. Dynamic Screen Mesh & Texture
    if (typeof document === 'undefined') {
      this.phoneTexture = new THREE.CanvasTexture({} as HTMLCanvasElement);
      this.phoneGroup.add(suctionCup, arm, phoneBody);
      this.group.add(this.phoneGroup);
      return;
    }
    this.phoneCanvas = document.createElement('canvas');
    this.phoneCanvas.width = 512;
    this.phoneCanvas.height = 1024;
    this.phoneTexture = new THREE.CanvasTexture(this.phoneCanvas);
    const screenMat = new THREE.MeshBasicMaterial({ map: this.phoneTexture });

    this.phoneScreenMesh = new THREE.Mesh(new THREE.PlaneGeometry(0.090, 0.178), screenMat);
    this.phoneScreenMesh.position.z = 0.005;
    phoneBody.add(this.phoneScreenMesh);

    this.phoneGroup.add(suctionCup, arm, phoneBody);
    this.group.add(this.phoneGroup);

    this.updatePhoneScreen('Fahrstunde: Grundausbildung', 'Altstadt-Zentrum', 100, true);
  }

  public updatePhoneScreen(missionTitle: string, districtName: string, score: number, isDriving: boolean = true) {
    if (!this.phoneCanvas) return;
    const ctx = this.phoneCanvas.getContext('2d');
    if (!ctx) return;

    // 1. App Background
    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(0, 0, 512, 1024);

    // 2. Top System Status Bar
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(0, 0, 512, 54);
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 22px sans-serif';
    ctx.fillText('16:30', 32, 36);
    ctx.font = '18px sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText('LTE 4G   🔋 75%', 480, 36);
    ctx.textAlign = 'left';

    // 3. Green App Header (Matching reference image: "TRANSPORTATION")
    ctx.fillStyle = '#15803d';
    ctx.fillRect(0, 54, 512, 90);
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 32px sans-serif';
    ctx.fillText('FAHRSCHULE STVO', 32, 112);

    // 4. District Selection Header
    ctx.fillStyle = '#1e293b';
    ctx.font = 'bold 22px sans-serif';
    ctx.fillText('Fahrprüfungs-Bezirk', 32, 185);

    // Option items
    const districts = [
      { name: '1. Altstadt (Zone 30)', active: districtName.includes('Altstadt') || true },
      { name: '2. Hauptboulevard (Tempo 50)', active: false },
      { name: '3. Kreisverkehr & Vorfahrt', active: false },
    ];

    let yOffset = 220;
    districts.forEach((d) => {
      ctx.fillStyle = d.active ? '#dcfce7' : '#ffffff';
      ctx.strokeStyle = d.active ? '#22c55e' : '#e2e8f0';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.roundRect(24, yOffset, 464, 68, 8);
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = d.active ? '#15803d' : '#334155';
      ctx.font = 'bold 22px sans-serif';
      ctx.fillText(d.name, 48, yOffset + 42);

      // Checkmark icon
      if (d.active) {
        ctx.fillStyle = '#16a34a';
        ctx.beginPath();
        ctx.arc(448, yOffset + 34, 16, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 18px sans-serif';
        ctx.fillText('✓', 440, yOffset + 40);
      }
      yOffset += 86;
    });

    // 5. Active Mission & Live Score Rating
    ctx.fillStyle = '#1e293b';
    ctx.font = 'bold 22px sans-serif';
    ctx.fillText('Aktuelle Bewertung', 32, yOffset + 40);

    // Score progress bar
    const normScore = Math.min(100, Math.max(0, score));
    ctx.fillStyle = '#e2e8f0';
    ctx.fillRect(24, yOffset + 60, 464, 28);

    ctx.fillStyle = normScore > 75 ? '#22c55e' : normScore > 50 ? '#f59e0b' : '#ef4444';
    ctx.fillRect(24, yOffset + 60, (464 * normScore) / 100, 28);

    ctx.fillStyle = '#0f172a';
    ctx.font = 'bold 20px sans-serif';
    ctx.fillText(`Score: ${Math.round(score)} / 100 Pkt`, 32, yOffset + 122);

    // Mission Details Card
    ctx.fillStyle = '#f1f5f9';
    ctx.beginPath();
    ctx.roundRect(24, yOffset + 150, 464, 180, 10);
    ctx.fill();

    ctx.fillStyle = '#0f172a';
    ctx.font = 'bold 22px sans-serif';
    ctx.fillText('Aufgabe:', 44, yOffset + 190);
    ctx.font = '20px sans-serif';
    ctx.fillStyle = '#334155';
    ctx.fillText(missionTitle.slice(0, 32), 44, yOffset + 225);
    ctx.fillText('StVO § 1 Abs. 2 einhalten!', 44, yOffset + 260);
    ctx.fillText(`Fahrlehrer: Herr Müller`, 44, yOffset + 295);

    // 6. Action Button at Bottom
    ctx.fillStyle = isDriving ? '#16a34a' : '#2563eb';
    ctx.beginPath();
    ctx.roundRect(24, 910, 464, 76, 12);
    ctx.fill();

    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 26px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(isDriving ? 'FAHRT AKTIV' : 'START OPERATION', 256, 958);
    ctx.textAlign = 'left';

    this.phoneTexture.needsUpdate = true;
  }

  /**
   * 7. Mirrors - 3D Interior Rearview Mirror mounted on windshield header & 3D Door Mirrors near A-pillars
   */
  private createMirrors() {
    this.mirrorGroup = new THREE.Group();
    const mirrorHousingMat = new THREE.MeshStandardMaterial({ color: 0x0f172a, roughness: 0.6, metalness: 0.2 });
    const capPaintMat = new THREE.MeshStandardMaterial({ color: 0xf8fafc, roughness: 0.35, metalness: 0.5 }); // Body paint for side mirror caps
    const chromeMat = new THREE.MeshStandardMaterial({ color: 0xd4d4d8, roughness: 0.2, metalness: 0.85 });
    const mirrorGlassMat = new THREE.MeshStandardMaterial({
      color: 0xe2e8f0,
      metalness: 0.95,
      roughness: 0.05,
    });

    // 1. Innenspiegel (Windshield Header Hanging Rearview Mirror — Degener 360° SimDrive standard)
    // Compact physical Golf mirror (~24 cm wide, ~7 cm tall) hanging on stem from windshield header, overlapping only the sky strip
    this.interiorMirrorGroup = new THREE.Group();
    this.interiorMirrorGroup.position.set(0.0, 1.40, 0.82);
    this.interiorMirrorGroup.rotation.y = -0.16; // Angled towards driver at +0.38
    this.interiorMirrorGroup.rotation.x = 0.04;

    // Mounting stem hanging down from ceiling / header (y=1.42 down to y=1.40)
    const stemGeom = new THREE.CylinderGeometry(0.006, 0.008, 0.05, 12);
    const stem = new THREE.Mesh(stemGeom, mirrorHousingMat);
    stem.position.set(0, 0.035, 0.008);
    stem.rotation.x = -0.25;
    this.interiorMirrorGroup.add(stem);

    // Ball joint mount
    const jointGeom = new THREE.SphereGeometry(0.011, 12, 12);
    const joint = new THREE.Mesh(jointGeom, mirrorHousingMat);
    joint.position.set(0, 0.008, 0.004);
    this.interiorMirrorGroup.add(joint);

    // Rounded mirror casing (24cm x 7.2cm x 1.8cm)
    const intBack = new THREE.Mesh(new THREE.BoxGeometry(0.24, 0.072, 0.018), mirrorHousingMat);
    this.interiorMirrorGroup.add(intBack);

    // Slim inner chrome bezel
    const intBezel = new THREE.Mesh(new THREE.BoxGeometry(0.236, 0.068, 0.004), chromeMat);
    intBezel.position.z = -0.008;
    intBack.add(intBezel);

    // Live reflection glass quad (23.2cm x 6.4cm — satisfies Degener standard 0.20-0.30m width, 0.05-0.10m height)
    const intGlassGeom = new THREE.PlaneGeometry(0.232, 0.064);
    const intUv = intGlassGeom.attributes.uv;
    for (let i = 0; i < intUv.count; i++) {
      intUv.setX(i, 1 - intUv.getX(i));
    }
    this.interiorMirrorMesh = new THREE.Mesh(intGlassGeom, mirrorGlassMat);
    this.interiorMirrorMesh.position.z = -0.010;
    this.interiorMirrorMesh.rotation.y = Math.PI;
    intBack.add(this.interiorMirrorMesh);

    this.mirrorGroup.add(this.interiorMirrorGroup);

    // 2. Linker Außenspiegel (Left Side Mirror — Driver Door Sill outside A-Pillar: x = +0.79)
    // Cleanly in frame on the left edge of Cockpit [V] with aerodynamic cap & live rear reflection
    const leftMirrorGroup = new THREE.Group();
    leftMirrorGroup.position.set(0.79, 0.94, 0.62);
    leftMirrorGroup.rotation.y = 0.55; // Angled inward to face driver at +0.38
    leftMirrorGroup.rotation.x = 0.02;

    // Door triangle mounting bracket extending from door outer skin
    const bracketGeom = new THREE.BoxGeometry(0.04, 0.04, 0.06);
    const bracketL = new THREE.Mesh(bracketGeom, mirrorHousingMat);
    bracketL.position.set(-0.02, -0.01, -0.02);
    leftMirrorGroup.add(bracketL);

    // Body-colored aerodynamic mirror cap / housing (14cm x 8.5cm x 4.5cm)
    const sideCapGeom = new THREE.BoxGeometry(0.14, 0.085, 0.045);
    const sideCapL = new THREE.Mesh(sideCapGeom, capPaintMat);
    leftMirrorGroup.add(sideCapL);

    // Mirror glass (13cm x 7.8cm) facing driver with inverted UV for real reflection
    const leftGlassGeom = new THREE.PlaneGeometry(0.13, 0.078);
    const leftUv = leftGlassGeom.attributes.uv;
    for (let i = 0; i < leftUv.count; i++) {
      leftUv.setX(i, 1 - leftUv.getX(i));
    }
    this.leftMirrorMesh = new THREE.Mesh(leftGlassGeom, mirrorGlassMat);
    this.leftMirrorMesh.position.z = -0.024;
    this.leftMirrorMesh.rotation.y = Math.PI;
    sideCapL.add(this.leftMirrorMesh);

    this.mirrorGroup.add(leftMirrorGroup);

    // 3. Rechter Außenspiegel (Right Side Mirror — Passenger Door Sill outside A-Pillar: x = -0.79)
    const rightMirrorGroup = new THREE.Group();
    rightMirrorGroup.position.set(-0.79, 0.94, 0.62);
    rightMirrorGroup.rotation.y = -0.55; // Angled inward towards driver
    rightMirrorGroup.rotation.x = 0.02;

    const bracketR = new THREE.Mesh(bracketGeom, mirrorHousingMat);
    bracketR.position.set(0.02, -0.01, -0.02);
    rightMirrorGroup.add(bracketR);

    const sideCapR = new THREE.Mesh(sideCapGeom, capPaintMat);
    rightMirrorGroup.add(sideCapR);

    const rightGlassGeom = new THREE.PlaneGeometry(0.13, 0.078);
    const rightUv = rightGlassGeom.attributes.uv;
    for (let i = 0; i < rightUv.count; i++) {
      rightUv.setX(i, 1 - rightUv.getX(i));
    }
    this.rightMirrorMesh = new THREE.Mesh(rightGlassGeom, mirrorGlassMat);
    this.rightMirrorMesh.position.z = -0.024;
    this.rightMirrorMesh.rotation.y = Math.PI;
    sideCapR.add(this.rightMirrorMesh);

    this.mirrorGroup.add(rightMirrorGroup);

    this.group.add(this.mirrorGroup);
  }

  private createInteriorSeats() {
    const seatMat = new THREE.MeshStandardMaterial({ color: 0x334155, roughness: 0.9 });
    const seatBeltMat = new THREE.MeshStandardMaterial({ color: 0x1e293b, roughness: 0.8 });

    // Driver Seat (Left = +0.38)
    const driverSeatBase = new THREE.Mesh(new THREE.BoxGeometry(0.48, 0.22, 0.48), seatMat);
    driverSeatBase.position.set(0.38, 0.25, -0.32);

    const driverSeatBack = new THREE.Mesh(new THREE.BoxGeometry(0.46, 0.65, 0.14), seatMat);
    driverSeatBack.position.set(0.38, 0.65, -0.52);
    driverSeatBack.rotation.x = -0.12;

    const driverHeadrest = new THREE.Mesh(new THREE.BoxGeometry(0.36, 0.18, 0.12), seatMat);
    driverHeadrest.position.set(0.38, 1.02, -0.54);
    driverHeadrest.rotation.x = -0.12;

    const beltDriver = new THREE.Mesh(new THREE.BoxGeometry(0.024, 0.45, 0.008), seatBeltMat);
    beltDriver.position.set(0.14, 0.72, -0.48);
    beltDriver.rotation.z = -0.22;
    beltDriver.rotation.x = -0.08;

    this.group.add(driverSeatBase, driverSeatBack, driverHeadrest, beltDriver);

    // Passenger / Examiner Seat (Right = -0.38)
    const passSeatBase = driverSeatBase.clone();
    passSeatBase.position.x = -0.38;
    const passSeatBack = driverSeatBack.clone();
    passSeatBack.position.x = -0.38;
    const passHeadrest = driverHeadrest.clone();
    passHeadrest.position.x = -0.38;

    this.group.add(passSeatBase, passSeatBack, passHeadrest);

    // 3D Examiner / Fahrlehrer Müller (passenger seat = -0.38)
    this.examinerGroup = new THREE.Group();
    this.examinerGroup.position.set(-0.38, 0.30, -0.40);

    const examBodyMat = new THREE.MeshStandardMaterial({ color: 0x1d4ed8, roughness: 0.8 });
    const examShirtMat = new THREE.MeshStandardMaterial({ color: 0xf8fafc, roughness: 0.9 });
    const examSkinMat = new THREE.MeshStandardMaterial({ color: 0xd4a373, roughness: 0.6 });
    const examHairMat = new THREE.MeshStandardMaterial({ color: 0x3b1a08, roughness: 0.8 });
    const clipboardMat = new THREE.MeshStandardMaterial({ color: 0xd97706, roughness: 0.5 });
    const paperMat = new THREE.MeshBasicMaterial({ color: 0xfef3c7 });

    const torso = new THREE.Mesh(new THREE.BoxGeometry(0.38, 0.52, 0.26), examBodyMat);
    torso.position.set(0, 0.26, 0);

    const collar = new THREE.Mesh(new THREE.BoxGeometry(0.10, 0.10, 0.05), examShirtMat);
    collar.position.set(0, 0.50, 0.13);

    const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.052, 0.10, 12), examSkinMat);
    neck.position.set(0, 0.57, 0);

    const head = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.26, 0.22), examSkinMat);
    head.position.set(0, 0.74, 0.01);
    head.rotation.y = -0.28;

    const hair = new THREE.Mesh(new THREE.BoxGeometry(0.23, 0.09, 0.22), examHairMat);
    hair.position.set(0, 0.86, 0.00);
    hair.rotation.y = -0.28;

    const earL = new THREE.Mesh(new THREE.BoxGeometry(0.03, 0.06, 0.06), examSkinMat);
    earL.position.set(-0.12, 0.73, 0.01);
    const earR = new THREE.Mesh(new THREE.BoxGeometry(0.03, 0.06, 0.06), examSkinMat);
    earR.position.set(0.12, 0.73, 0.01);

    const armL = new THREE.Mesh(new THREE.BoxGeometry(0.10, 0.38, 0.12), examBodyMat);
    armL.position.set(-0.20, 0.14, 0.06);
    armL.rotation.z = 0.18;
    const handL = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.10, 0.06), examSkinMat);
    handL.position.set(-0.22, -0.05, 0.06);

    const armR = new THREE.Mesh(new THREE.BoxGeometry(0.10, 0.36, 0.12), examBodyMat);
    armR.position.set(0.20, 0.10, 0.04);
    armR.rotation.z = -0.22;
    armR.rotation.x = 0.15;
    const handR = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.10, 0.06), examSkinMat);
    handR.position.set(0.23, -0.04, 0.04);

    const clipBoard = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.30, 0.015), clipboardMat);
    clipBoard.position.set(-0.04, 0.02, 0.14);
    clipBoard.rotation.x = -0.45;
    const paper = new THREE.Mesh(new THREE.PlaneGeometry(0.18, 0.24), paperMat);
    paper.position.set(-0.04, 0.02, 0.148);
    paper.rotation.x = -0.45;

    const examBelt = new THREE.Mesh(new THREE.BoxGeometry(0.022, 0.40, 0.008), seatBeltMat);
    examBelt.position.set(0.16, 0.48, 0.12);
    examBelt.rotation.z = -0.22;

    this.examinerGroup.add(
      torso, collar, neck, head, hair, earL, earR,
      armL, handL, armR, handR,
      clipBoard, paper, examBelt
    );

    this.group.add(this.examinerGroup);
  }

  public update(
    steeringAngle: number,
    speedKmh: number,
    rpm: number,
    blinkerState: string,
    handbrake: boolean,
    isStalled: boolean,
    gearDisplay: string = 'D',
    missionTitle?: string,
    score?: number,
    deltaTime: number = 0.016
  ) {
    // 1. Steering Wheel Visual Rotation
    this.steeringWheel.rotation.z = -steeringAngle * 2.8;

    // Dial Inertia & Damping for realistic mechanical needles:
    const absSpeed = Math.abs(speedKmh);
    const targetRpm = rpm;
    // Fast responsive needle tracking with realistic mechanical dampening:
    this.displaySpeedKmh += (absSpeed - this.displaySpeedKmh) * Math.min(1.0, deltaTime * 12.0);
    this.displayRpm += (targetRpm - this.displayRpm) * Math.min(1.0, deltaTime * 14.0);

    // If actual speed is near 0 and display speed is tiny (< 0.2 km/h), snap to 0
    if (absSpeed < 0.1 && this.displaySpeedKmh < 0.2) {
      this.displaySpeedKmh = 0;
    }

    // 2. Speed Needle Angle (0 to 260 km/h maps from +2.2 rad to -2.2 rad)
    const speedRatio = Math.min(1.0, Math.max(0, this.displaySpeedKmh / 260));
    const speedAngle = 2.2 - speedRatio * 4.4;
    this.speedNeedle.rotation.z = speedAngle;

    // 3. RPM Needle Angle (800 to 8000 RPM maps from +2.2 rad to -2.2 rad)
    const rpmRatio = Math.min(1.0, Math.max(0, (this.displayRpm - 800) / 7200));
    const rpmAngle = 2.2 - rpmRatio * 4.4;
    this.rpmNeedle.rotation.z = rpmAngle;

    // 4. Digital Cluster updates (Speed & Tachometer OLED with Engine on status)
    // Synchronized with damped display speed so numbers count down smoothly
    const roundedSpeed = Math.round(this.displaySpeedKmh);
    if (this.lastPhoneGear !== gearDisplay || this.lastPhoneSpeed !== roundedSpeed) {
      this.lastPhoneGear = gearDisplay;
      this.lastPhoneSpeed = roundedSpeed;
      this.renderDigitalCluster(gearDisplay, roundedSpeed, !isStalled);
    }

    // 5. Update Blinker Lamps & 3D Indicator Light Arrows
    const isBlinkOn = blinkerState !== 'off' && Math.floor(Date.now() / 380) % 2 === 0;
    const leftActive = (blinkerState === 'left' || blinkerState === 'hazard') && isBlinkOn;
    const rightActive = (blinkerState === 'right' || blinkerState === 'hazard') && isBlinkOn;

    if (this.lastBlinkPhase !== isBlinkOn || this.lastBlinkerState !== blinkerState) {
      this.lastBlinkPhase = isBlinkOn;
      this.lastBlinkerState = blinkerState;

      if (this.blinkerLeftCanvas) {
        this.drawBlinkerArrow(this.blinkerLeftCanvas, true, leftActive);
        this.blinkerLeftTexture.needsUpdate = true;
      }
      if (this.blinkerRightCanvas) {
        this.drawBlinkerArrow(this.blinkerRightCanvas, false, rightActive);
        this.blinkerRightTexture.needsUpdate = true;
      }
    }

    if (this.blinkerLeftLamp?.material) {
      (this.blinkerLeftLamp.material as THREE.MeshBasicMaterial).color.setHex(leftActive ? 0x22c55e : 0x14532d);
    }
    if (this.blinkerRightLamp?.material) {
      (this.blinkerRightLamp.material as THREE.MeshBasicMaterial).color.setHex(rightActive ? 0x22c55e : 0x14532d);
    }

    // 6. Handbrake & Stall Warning
    (this.handbrakeLamp.material as THREE.MeshBasicMaterial).color.setHex(handbrake ? 0xef4444 : 0x450a0a);
    (this.stallWarningLamp.material as THREE.MeshBasicMaterial).color.setHex(isStalled ? 0xeab308 : 0x422006);

    // 7. Refresh Smartphone Screen if score changed
    if (score !== undefined && Math.round(score) !== this.lastPhoneScore) {
      this.lastPhoneScore = Math.round(score);
      this.updatePhoneScreen(missionTitle || 'Grundausbildung StVO', 'Altstadt-Zentrum', score, true);
    }
  }
}
